import { Star, BadgeCheck, ShieldCheck, Medal } from "lucide-react";

export function scoreColor(score) {
  if (score >= 90) return "#10B981"; // emerald  — excellent (90–100)
  if (score >= 50) return "#06B6D4"; // cyan     — good     (50–89)
  if (score >= 30) return "#F59E0B"; // amber    — average  (30–49)
  return "#F97316";                  // orange   — low      (0–29)
}

export function RankBadge({ rank, boosted, testId }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
      <span
        data-testid={testId || "rank-position-badge"}
        className="font-mono2 text-xs sm:text-base font-bold text-emerald-400 border border-emerald-500/40 bg-emerald-500/5 rounded-md px-2 py-0.5 sm:px-2.5 sm:py-1 self-start"
      >
        #{rank}
      </span>
      {boosted && (
        <span
          data-testid="boosted-badge"
          className="font-mono2 text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded-md px-2 py-1 self-start"
        >
          Boosted
        </span>
      )}
    </div>
  );
}

export function ScoreDonut({ score, size = 64, stroke = 5, testId }) {
  if (score === null || score === undefined) {
    return (
      <div
        data-testid={testId || "score-not-yet-scored"}
        className="flex items-center justify-center text-center font-mono2 uppercase tracking-widest leading-[1.15] text-slate-500 border border-white/10 bg-slate-500/10 rounded-full shrink-0"
        style={{ width: size, height: size, fontSize: Math.max(size * 0.155, 6) }}
      >
        Not yet<br />scored
      </div>
    );
  }
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const color = scoreColor(score);
  return (
    <div data-testid={testId || "highrank-score-badge"} className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--donut-track, rgba(255,255,255,0.08))" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - score / 100)}
          style={{ transition: "stroke-dashoffset 0.8s cubic-bezier(0.16,1,0.3,1)", filter: `drop-shadow(0 0 6px ${color})` }}
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center font-mono2 font-bold"
        style={{ color, fontSize: size * 0.28 }}
      >
        {score}
      </span>
    </div>
  );
}

export function ClaimBadge({ claimed, testId }) {
  if (claimed) {
    return (
      <span
        data-testid={testId || "claimed-badge"}
        className="inline-flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5"
      >
        <BadgeCheck size={10} /> Claimed
      </span>
    );
  }
  return (
    <span
      data-testid={testId || "unclaimed-badge"}
      className="inline-flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-widest text-slate-400 border border-white/15 bg-slate-500/10 rounded px-1.5 py-0.5"
    >
      Unclaimed
    </span>
  );
}

export function VerifiedBadge({ testId }) {
  return (
    <span
      data-testid={testId || "verified-badge"}
      className="inline-flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-widest text-cyan-400 border border-cyan-500/40 bg-cyan-500/10 rounded px-1.5 py-0.5"
    >
      <ShieldCheck size={10} /> Verified
    </span>
  );
}

export function ProAlumniBadge({ testId }) {
  return (
    <span
      data-testid={testId || "pro-alumni-badge"}
      className="inline-flex items-center gap-1 font-mono2 text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/40 bg-amber-500/10 rounded px-1.5 py-0.5"
    >
      <Medal size={10} /> Pro Alumni
    </span>
  );
}

const AVATAR_COLORS = ["#3FE08C", "#06B6D4", "#F59E0B", "#8B5CF6", "#EC4899", "#10B981", "#F97316"];

export function LogoAvatar({ name, logoUrl, size = 36, testId }) {
  if (logoUrl) {
    return (
      <span
        data-testid={testId}
        className="shrink-0 rounded-lg border border-white/10 bg-[#0A0A09] flex items-center justify-center overflow-hidden"
        style={{ width: size, height: size }}
      >
        <img src={logoUrl} alt={`${name} logo`} className="w-full h-full object-contain p-1" />
      </span>
    );
  }
  const initial = (name || "?").trim().slice(0, 1).toUpperCase();
  const color = AVATAR_COLORS[(name || "").split("").reduce((a, ch) => a + ch.charCodeAt(0), 0) % AVATAR_COLORS.length];
  return (
    <span
      data-testid={testId}
      className="shrink-0 rounded-lg flex items-center justify-center font-heading font-bold"
      style={{ width: size, height: size, backgroundColor: color, color: "#050505", fontSize: size * 0.45 }}
    >
      {initial}
    </span>
  );
}

export function Stars({ value = 0, onSelect, size = 15, testId }) {
  return (
    <div data-testid={testId || "star-rating"} className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= Math.round(value);
        const cls = filled ? "fill-emerald-400 text-emerald-400" : "text-slate-600";
        return onSelect ? (
          <button
            key={i}
            type="button"
            data-testid={`star-select-${i}`}
            onClick={() => onSelect(i)}
            className="transition-transform duration-150 hover:scale-125"
          >
            <Star size={size + 3} className={cls} />
          </button>
        ) : (
          <Star key={i} size={size} className={cls} />
        );
      })}
    </div>
  );
}
